import { ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup } from "./GlassInput";
import { useContext, useEffect, useMemo, useState } from "react";
import { MapitStateContext } from "src/states/MapitState";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { DataDivisionType, LayerInfo } from "src/common/managers/Types";
import { Typeahead } from "react-bootstrap-typeahead";
import { Localization } from "@viamap/viamap2-common";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import jsPDF, { TextOptionsLight } from "jspdf";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { DataCollector } from "src/dataCollector/DataCollector";
import chroma from "chroma-js";
import { VLine } from "./MitGlassComponents";
import { Spinner } from "react-bootstrap";
import { message } from "./Message";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { wrapText } from "src/functions/PaperCanvas";
import { aktIdFormat } from "./DatarapportShared";

export function ServitutRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onGenerate: (data: any, pdf: jsPDF) => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);

   const [loading, setLoading] = useState(false);


   let usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter(layer => layer.propertiesInGeoJson?.includes("SFEnummer"));
   }, [mapitState.layers])

   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((a) => a.layerId).includes(b)));
   }, [mapitState.layers])

   let colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a])
      );
   }, [layerId])

   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        let value = e.currentTarget.value;
                        props.onSelectRapport(value);
                     }}>
                     {
                        props.availableRapports.map((a) => {
                           return (
                              <option key={a.value} value={a.value}>{a.label}</option>
                           )
                        })
                     }
                  </select>
               </GlassInputControl>
               <label>{Localization.getText("Layers")}</label>
               <Typeahead
                  multiple={true}
                  id="layerTypeahead"
                  options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                  labelKey={"datasetname"}
                  placeholder={Localization.getText("please select") + " " + Localization.getText("layer")}
                  emptyLabel={Localization.getText("No results found")}
                  onChange={(selected) => setLayerId(selected.map((a: any) => a.id))}
                  selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => layerId.includes(a.id))}
               />
               <label></label>
               <GlassButtonRow>
                  <GlassButton onClick={() => setLayerId(usableLayers.map((a) => a.layerId))}>{Localization.getText("Select All")}</GlassButton>
                  <GlassButton onClick={() => setLayerId([])}>{Localization.getText("Remove All")}</GlassButton>
               </GlassButtonRow>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => {
                     return (
                        <div
                           key={a.sfe}
                           title={a.sfe + ""}
                           style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}>
                           <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                        </div>
                     )
                  })}


               </div>
            </GlassInputGroup>


         </ADModalBody>
         <ADModalFooter>
            {loading ? <Spinner /> : <GlassButton disabled={!colorList.length} onClick={async () => {
               setLoading(true);
               try {
                  let data = await hentDataOgBelligenhed(colorList)
                  let pdf = createPDF(data, colorList);
                  if (pdf) {
                     props.onGenerate(data, pdf);
                  }
               } catch (e: unknown) {
                  if (e instanceof Error) {
                     message.error(Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: e.message }));
                  } else {
                     message.error(Localization.getText("An error occurred while generating the report. Please try again later or contact support."));
                  }
                  setLoading(() => false);
               }
            }}
            >{Localization.getText("Generate")}</GlassButton>}
         </ADModalFooter>
      </>
   );
}

async function hentData(liste: { sfe: number }[]) {
   let x = Promise.all(liste.map(async (a) => {
      return await PropertyInfoInterface.getDataTingbogByBFE(a.sfe.toString());
   }))
   return x;
}

async function hentBeliggenhed(liste: { sfe: number }[]) {
   let request = liste.map((a) => a.sfe.toString());
   let EBR = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt(request);
   return EBR.map((a) => a.betegnelse);
}

async function hentDataOgBelligenhed(liste: { sfe: number }[]) {
   let data = await hentData(liste);
   let adresser = await hentBeliggenhed(liste);
   return data.map((item, idx) => ({ ...item, adresse: adresser[idx], BFEnummer: liste[idx].sfe }));
}



function SFEAndColorList(layerInfoArr: LayerInfo[]) {
   let list: { sfe: number, color: string }[] = []
   let sfeList = new Set<number>();
   let validLayers = layerInfoArr.filter(layer => layer.propertiesInGeoJson?.includes("SFEnummer"));

   validLayers.forEach(layer => {
      let getColor = (_p) => layer.styling.color && chroma(layer.styling.color).hex("rgb") || "black";
      if (layer.styling.colorByProperty) {
         if (layer.styling.colorByValue?.divisions?.type == DataDivisionType.Discrete) {
            let style = layer.styling
            getColor = (properties) => {
               if (style.colorByProperty) {
                  let x = GenerateGeomUtils.getColorByValueDiscrete(undefined as any, properties[style.colorByProperty], style.colorByValue?.divisions, style.outsideColorRange);
                  return x.color && chroma(x.color).hex("rgb") || "black";
               }
               return "black";
            }
         } else if (layer.styling.colorByValue?.divisions?.type == DataDivisionType.Continous) {
            let style = layer.styling
            getColor = (properties) => {
               if (style.colorByProperty) {
                  let x = GenerateGeomUtils.getColorByValueNummeric(undefined as any, properties[style.colorByProperty], style.colorByValue?.divisions, style.outsideColorRange);
                  return x.color && chroma(x.color).hex("rgb") || "black";
               }
               return "black";
            }
         }
      }
      layer.geoJson?.features?.forEach(feature => {
         let sfe = feature.properties?.SFEnummer;
         if (sfe && typeof sfe === "string") {
            sfe = parseInt(sfe)
         }
         if (sfe && !sfeList.has(sfe)) {
            sfeList.add(sfe);
            list.push({ sfe: sfe, color: getColor(feature.properties) });
         }
      });
   });
   return list;
}

function createPDF(data: any[], colorList: { sfe: number, color: string }[]): jsPDF | null {

   let doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });


   const servitutSort = (a, b) => {
      return (parseInt(a.prioritetNummer) || 0) - (parseInt(b.prioritetNummer) || 0);
   };

   if (data.length == 0) {
      return null;
   }

   doc.text("Servitutoversigt", 110, 10, { align: "center" });
   doc.setFontSize(7);
   doc.text("Servituttabel", 15, 10);
   doc.text("Mapit", 15, 13);
   doc.text("Dato: " + new Date().toLocaleDateString("da-dk"), 220 - 25, 10, { align: "right" });
   doc.text("Side: 1", 220 - 25, 13, { align: "right" });
   let currentPage = 1;

   if (data.length == 0) {
      return doc;
   }

   let top = 15;
   let left = 15;
   let height = 5;

   let headerHeight = 14;
   doc.setFontSize(12);
   data.forEach((a: {
      BFEnummer: string, adresse: string, servitutter: {
         aktAliasId: string;
         tekstSummarisk: string; prioritetNummer: string, tekster: string[], aktId: string
      }[]
   }) => {

      let servitutter = a.servitutter ?? [defaultMissingServitut()].toSorted(servitutSort);
      if (servitutter.length == 0) {
         servitutter = [defaultMissingServitut()];
      }

      let fnLeng = (serv: typeof servitutter) => serv.reduce((acc, cur) => acc + wrapText(doc.canvas.getContext() as unknown as CanvasRenderingContext2D, (cur.tekstSummarisk || cur.tekster.join(", ")), 600).length + 1, 0) * height + headerHeight;
      let length = fnLeng(servitutter);
      let resP = [...servitutter];
      let parts = splitIntoParts(resP, 260, fnLeng)
      let start = top;
      parts.forEach((part, idx) => {
         length = fnLeng(part) - (idx ? 2 * height - 1 : 0);

         if (top + length > 260 && top !== 15) {
            doc.cellAddPage();
            currentPage++;
            doc.setFontSize(7);
            doc.setFont(undefined as any, "normal");
            doc.text("Servitutoversigt", 15, 10);
            doc.text("Mapit", 15, 13);
            doc.text("Dato: " + new Date().toLocaleDateString("da-dk"), 220 - 25, 10, { align: "right" });
            doc.text("Side: " + currentPage, 220 - 25, 13, { align: "right" });
            top = 15;
         } else if (idx > 0) {
            top += 2;
            doc.setFillColor("#000000");
         }
         start = top;
         // doc.cellAddPage();
         if (idx == 0) {
            doc.setFontSize(12);
            customCell(doc, left, top, 10, 14, " ", 0, "left");
            customCell(doc, left + 10, top, 170, 14, a.adresse, 0, "left");
            doc.setFontSize(9);
            doc.text("SFE: " + a.BFEnummer, 27, top + 7.5);
            top += 9;
         }
         doc.setFontSize(12);

         customCell(doc, left, top, 10, height, " ", 1, "left")
         customCell(doc, left + 10, top, 14, height, "Nr.", 1, "left", true)
         customCell(doc, left + 24, top, 156, height, "Servitut", 1, "left", true)

         part.forEach((a, idx) => {
            let strings = wrapText(doc.canvas.getContext() as unknown as CanvasRenderingContext2D, (a.tekstSummarisk || a.tekster.join(", ")), 580);
            let calcHeight = height * (1 + strings.length);

            if (Array.isArray(a)) {
               return; // we except an object.
            }
            top += 5;
            customCell(doc, left, top, 10, calcHeight, " ", 2 + idx, "left")
            doc.setFontSize(20);
            customCell(doc, left + 10, top, 14, calcHeight, " ", 2 + idx, "left")
            doc.text(a.prioritetNummer, left + 17, top + 8, { align: "center" });
            doc.setFontSize(12);
            doc.text(aktIdFormat(a.aktId || a.aktAliasId), left + 24 + 2, top + 4);
            customCell(doc, left + 24, top, 156, calcHeight, "", 2 + idx, "left")
            strings.forEach((line, lineIdx) => {
               doc.text(line, left + 24 + 2, top + 9);
               top += 5;
            })
         });
         let aColor = colorList.find((b) => b.sfe == parseInt(a.BFEnummer))?.color
         if (aColor) doc.setFillColor(aColor);
         doc.rect(15, start, 10, length, "FD");
      });
      top += 5;
      let aColor = colorList.find((b) => b.sfe == parseInt(a.BFEnummer))?.color
      if (aColor) doc.setFillColor(aColor);
      doc.rect(15, start, 10, top - start, "FD");

      top += 10;
   });

   return doc;
}

function customCell(doc: jsPDF, x, y, width, height, value, ln, align?: TextOptionsLight["align"], bold?: boolean) {
   doc.setDrawColor("#000000");
   doc.rect(x, y, width, height, "S");
   if (bold) {
      doc.setFont(undefined as any, "bold");
   } else {
      doc.setFont(undefined as any, "normal");
   }

   if (align == "right") {
      doc.text(value + "" || " ", x + width - 2, y + 4, { align: align || "left" });
   } else {
      doc.text(value + "" || " ", x + 2, y + 4, { align: align || "left", });
   }
}

function splitIntoParts<T>(arr: T[], maxLength: number, fn: (arr: T[]) => number): T[][] {
   let part = [arr];
   let i = 0;
   let parIdx = 0;
   if (fn(arr) > maxLength) {
      part = [[]];
      while (i < arr.length) {
         if (fn(part[parIdx]) > maxLength) {
            parIdx++;
            part.push([]);
         }
         part[parIdx].push(arr[i]);
         i++;
      }
      let minLength = 3 - part[parIdx].length
      if (minLength > 0) {
         part[parIdx].push(...part[parIdx - 1].slice(-minLength));
      }
   }
   return part;
}

function defaultMissingServitut(): {
   aktAliasId: string;
   tekstSummarisk: string; prioritetNummer: string, tekster: string[], aktId: string
} {
   return {
      aktAliasId: "-",
      aktId: "Ingen servitut",
      prioritetNummer: "?",
      tekstSummarisk: "Intet fundet for denne SFE",
      tekster: []
   }
}